import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IconEdit } from '@tabler/icons-react';
import { PinInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import useCleverTapEvents from 'src/events/useGetEvents';
import { EMAIL_EVENT } from 'src/events/constant';
import { RootState } from '../../redux/store';
import { ScreenWrapper } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import {
  resendEmailOtp,
  clearEmailFormError, setEmailFormStatus, setIsEmailOtpResent, setOtpSentStatus, verifyEmail,
} from './emailFormSlice';
import { useStyles } from './verifyEmail.styles';
import { Button } from '../../components/atoms';

export const VerifyEmail = () : JSX.Element => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const { email, isOtpResent, isOtpSent } = useSelector((state: RootState) => state.emailData);
  const emailDataState = useSelector((state: RootState) => state.emailData);
  const navigate = useNavigate();
  const onNextClick = () => {
    navigate('/');
  };
  const [otp, setOtp] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [timer, setTimer] = useState(30);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(otp.length !== 4);
  const { TriggerEvent } = useCleverTapEvents();

  const handleResendOtp = () => {
    dispatch(setIsEmailOtpResent(true));
    dispatch(resendEmailOtp({
      email,
    }));
    TriggerEvent(EMAIL_EVENT.resendOtp, { emailId: email, typeOfResend: "email", errorMessage });
    notifications.show({
      color: 'green',
      message: 'Successfully sent OTP!',
      icon: <IconSuccess />,
    });
    setOtp('');
    setTimer(30);
    setErrorMessage('');
  };

  const handleOtpChange = (val: any) => {
    setOtp(val.toString());
  };

  const onEditEmail = () => {
    navigate('/add-email', { replace: true });
  };

  const handleSubmitOtp = () => {
    dispatch(verifyEmail({
      email,
      otp,
    }));
    TriggerEvent(EMAIL_EVENT.emailOtp, { emailId: email || "", errorMessage: errorMessage || "" });
  };

  useEffect(() => {
    if (otp.length === 4) {
      handleSubmitOtp();
    }
    setErrorMessage('');
  }, [otp]);

  // to check for error state and show toast

  useEffect(() => {
    if (emailDataState.status === 'error') {
      setIsButtonDisabled(true);
      notifications.show({
        color: 'error',
        message: emailDataState.error.message,
        icon: <IconError />,
      });
    }
  }, [emailDataState.status]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    if (!isOtpSent) {
      navigate("/add-email", { replace: true });
    }
    return () => {
      clearInterval(interval);
      dispatch(setOtpSentStatus({ isOtpSent: false }));
      dispatch(setIsEmailOtpResent(false));
    };
  }, []);

  useEffect(() => {
    if (!isOtpResent) {
      if (emailDataState.status === "success") {
        notifications.show({
          color: 'green',
          message: 'Email Verified Successfully!',
          icon: <IconSuccess />,
        });
        navigate("/verify-pan");
        dispatch(setEmailFormStatus("idle"));
      } else if (emailDataState.status === "error") {
        setErrorMessage(emailDataState.error.message || "");
        dispatch(clearEmailFormError());
      }
    } else {
      if (emailDataState.status === "success") {
        notifications.show({
          color: 'green',
          message: 'Successfully resent OTP!',
          icon: <IconSuccess />,
        });
        dispatch(setEmailFormStatus("idle"));
      } else if (emailDataState.status === "error") {
        handleOtpChange("");
        notifications.show({
          color: 'red',
          message: emailDataState.error.message,
          icon: <IconError />,
        });
        dispatch(clearEmailFormError());
      }
      dispatch(setIsEmailOtpResent(false));
    }
  }, [emailDataState.status]);

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={4} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">
            Email OTP
          </h2>
          <div className={classes.descContainer}>
            <p className={classes.emailDescription}>
              {'Enter the OTP sent to '}
            </p>
            <button type="button" onClick={onEditEmail} className={classes.changeEmailLink}>
              {` ${email}`}
              <IconEdit size={18} />
            </button>

          </div>
          <div>
            <PinInput
              className={classes.otpInputStyledWrapper}
              classNames={{
                wrapper: classes.pinInputWrapper,
              }}
              onChange={handleOtpChange}
              autoFocus
              value={otp}
              inputMode="numeric"
              error={!!errorMessage}
              type="number"
              oneTimeCode
            />
          </div>
          {errorMessage && (
          <p className={cx(classes.inputDescription, classes.inputError)}>
            {errorMessage}
          </p>
          )}
          <p className={classes.inputDescription}>
            Having trouble?
            {' '}
            {timer > 0 ? (`Request a new OTP in 00:${timer < 10 ? `0${timer}` : timer}`) : (
              <button
                type="button"
                name="resend otp"
                className={classes.resend}
                onClick={handleResendOtp}
              >
                Resend OTP
              </button>
            )}
          </p>
        </div>
        <div className="csFooter">
          <Button
            disabled={isButtonDisabled}
            onClick={onNextClick}
            loading={emailDataState.status === 'loading'}
          >
            Next
          </Button>
        </div>
      </div>
    </ScreenWrapper>
  );
};

export default VerifyEmail;
